import { useState, useEffect } from "react";
import { useI18n } from "@/i18n/I18nProvider";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { getProgramStatus, sortProgramsChronological } from "@/data/mediaPrograms";
import { useEmissionsStore } from "@/data/emissionsStore";
import { getVideos, MediathequeVideo } from "@/data/mediathequeStore";
import { Tv, Radio, MonitorPlay, Clock, Calendar, Eye, Search, Filter, ArrowLeft, ArrowRight, Youtube, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import { CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const labels = {
  fr: {
    pageTitle: "Toutes les émissions",
    pageDesc: "Retrouvez l'intégralité des débats, interviews et émissions politiques.",
    searchPlaceholder: "Rechercher une émission...",
    filterType: "Type de média",
    filterTypeAll: "Tous les médias",
    filterTime: "Période",
    filterTimeAll: "Toutes périodes",
    filterToday: "Aujourd'hui",
    filterWeek: "Cette semaine",
    upcomingSection: "Programmes à venir",
    pastSection: "Émissions que vous avez peut-être ratées",
    noResults: "Aucune émission ne correspond à votre recherche.",
    tv: "TV", radio: "Radio", web: "Web", live: "En direct", imminent: "Dans moins d'1h", upcoming: "À venir", viewShow: "Fiche du programme", finished: "Terminée"
  },
  ar: {
    pageTitle: "جميع البرامج",
    pageDesc: "اطلع على كافة المناظرات والمقابلات والبرامج السياسية.",
    searchPlaceholder: "البحث عن برنامج...",
    filterType: "نوع الوسيلة",
    filterTypeAll: "جميع الوسائط",
    filterTime: "الفترة",
    filterTimeAll: "جميع الفترات",
    filterToday: "اليوم",
    filterWeek: "هذا الأسبوع",
    upcomingSection: "البرامج القادمة",
    pastSection: "برامج ربما فاتتك",
    noResults: "لا يوجد برنامج يطابق بحثك.",
    tv: "تلفزيون", radio: "راديو", web: "ويب", live: "مباشر", imminent: "أقل من ساعة", upcoming: "لاحقا", viewShow: "ورقة البرنامج", finished: "منتهية"
  },
  zgh: {
    pageTitle: "ⵎⴰⵕⵕⴰ ⵉⵖⴰⵡⴰⵙⵏ",
    pageDesc: "ⴰⴼ ⴰⴽⴽⵯ ⵉⵎⵙⴳⴷⴰⵍ ⴷ ⵉⵖⴰⵡⴰⵙⵏ ⵉⵙⵔⵜⴰⵏⵏ.",
    searchPlaceholder: "ⵔⵣⵓ ⵅⴼ ⵓⵖⴰⵡⴰⵙ...",
    filterType: "ⴰⵏⴰⵡ ⵏ ⵓⵎⵉⴷⵢⴰ",
    filterTypeAll: "ⵎⴰⵕⵕⴰ ⵉⵎⵉⴷⵢⴰⵜⵏ",
    filterTime: "ⴰⴽⵓⴷ",
    filterTimeAll: "ⵎⴰⵕⵕⴰ ⵉⴽⵓⴷⵏ",
    filterToday: "ⴰⵙⵙⴰ",
    filterWeek: "ⴰⵏⵎⵍⴰⵙⵙ ⴰⴷ",
    upcomingSection: "ⵉⵖⴰⵡⴰⵙⵏ ⴷ ⵢⵓⵛⴽⴰⵏ",
    pastSection: "ⵉⵖⴰⵡⴰⵙⵏ ⵉⵣⵔⵉⵏ",
    noResults: "ⵓⵔ ⵉⵍⵍⵉ ⵎⴰ ⵉⵎⵙⴰⵙⴰⵏ ⴷ ⵓⵔⵣⵣⵓ ⵏⵏⴽ.",
    tv: "ⵜⵉⵍⵉⴼⵉⵣⵢⵓⵏ", radio: "ⵕⵕⴰⴷⵢⵓ", web: "ⵡⵉⴱ", live: "ⴰⵙⵔⵉⴷ", imminent: "ⴷⴷⵓ 1 ⵜⵙⵔⴰⴳⵜ", upcoming: "ⴰⴷ ⵢⴰⵛⴽ", viewShow: "ⵜⴰⴼⵉⵛⵜ ⵏ ⵓⵖⴰⵡⴰⵙ", finished: "ⵜⵙⵎⴷ"
  }
};

const AllEmissionsPage = () => {
  const { lang, dir } = useI18n();
  const l = labels[lang as keyof typeof labels] || labels.fr;

  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterTime, setFilterTime] = useState("all");

  const [pastSearch, setPastSearch] = useState("");
  const [pastFilterChannel, setPastFilterChannel] = useState("all");
  const [pastFilterEmission, setPastFilterEmission] = useState("all");
  const [pastFilterMonth, setPastFilterMonth] = useState("all");

  const { emissions: programsInfo, fetchEmissions } = useEmissionsStore();

  useEffect(() => {
    fetchEmissions(true); // public only
  }, [fetchEmissions]);

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'tv': return <Tv className="w-4 h-4 mr-1.5" />;
      case 'radio': return <Radio className="w-4 h-4 mr-1.5" />;
      default: return <MonitorPlay className="w-4 h-4 mr-1.5" />;
    }
  };

  const getStatusBadge = (status: string) => {
    if (status === 'live') {
      return (
        <Badge className="bg-red-600 hover:bg-red-700 text-white animate-pulse shadow-[0_0_12px_rgba(220,38,38,0.6)] font-bold px-3 py-1 text-xs border-0">
          <span className="w-2 h-2 rounded-full bg-white mr-1.5" />{l.live}
        </Badge>
      );
    }
    if (status === 'imminent') {
      return (
        <Badge className="bg-orange-500 hover:bg-orange-600 text-white animate-pulse shadow-[0_0_10px_rgba(249,115,22,0.5)] font-bold px-3 py-1 text-xs border-0">
          <Clock className="w-3.5 h-3.5 mr-1" />{l.imminent}
        </Badge>
      );
    }
    if (status === 'finished') {
      return (
        <Badge className="bg-slate-500 hover:bg-slate-600 text-white animate-pulse shadow-[0_0_10px_rgba(100,116,139,0.6)] font-bold px-3 py-1 text-xs border-0 gap-1.5">
          <span className="w-2 h-2 rounded-full bg-white" />{l.finished}
        </Badge>
      );
    }
    if (status === 'upcoming') {
      return <Badge variant="outline" className="border-border bg-background/80 backdrop-blur-md border-dashed text-foreground px-3 py-1 text-xs font-bold">{l.upcoming}</Badge>;
    }
    return null;
  };

  const mediathequeVideos = getVideos().filter(v => v.published);

  const filteredPrograms = programsInfo.map(p => ({
    ...p,
    statusTracker: getProgramStatus(p.dateIso, p.durationMinutes)
  }))
  .filter(p => {
    if (!p.published) return false;
    // Text search
    const title = (lang === 'fr' ? p.frTitle : lang === 'ar' ? p.arTitle : p.zghTitle).toLowerCase();
    if (search && !title.includes(search.toLowerCase())) return false;
    
    // Type
    if (filterType !== 'all' && p.type !== filterType) return false;

    // Time filter (Today / Week logic)
    const today = new Date();
    const progDate = new Date(p.dateIso);
    if (filterTime === 'today') {
      if (progDate.toDateString() !== today.toDateString()) return false;
    } else if (filterTime === 'week') {
      const diffTime = Math.abs(progDate.getTime() - today.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      if (diffDays > 7) return false;
    }
    return true;
  });

  const getPoster = (prog: MediaProgram) => {
    if (prog.posterUrl && !prog.posterUrl.startsWith('blob:')) return prog.posterUrl;
    switch (prog.type) {
      case 'tv': return '/images/media/tv.png';
      case 'radio': return '/images/media/radio.png';
      case 'web': return '/images/media/web.png';
      default: return '/images/media/tv.png';
    }
  };

  const sortedPrograms = [...filteredPrograms].sort(sortProgramsChronological);
  const activePrograms = sortedPrograms.filter(p => p.statusTracker !== 'past');
  const pastPrograms = sortedPrograms.filter(p => p.statusTracker === 'past');

  const filteredPastPrograms = programsInfo.filter(p => p.published && getProgramStatus(p.dateIso, p.durationMinutes) === 'past')
    .filter(p => {
       const title = (lang === 'fr' ? p.frTitle : lang === 'ar' ? p.arTitle : p.zghTitle).toLowerCase();
       // Recherche par mot clé (incluant le titre, le sujet et la description)
       if (pastSearch) {
         const searchLower = pastSearch.toLowerCase();
         const subject = (lang === 'fr' ? p.subject_fr : lang === 'ar' ? p.subject_ar : p.subject_zgh)?.toLowerCase() || "";
         const desc = (lang === 'fr' ? p.description_fr : lang === 'ar' ? p.description_ar : p.description_zgh)?.toLowerCase() || "";
         if (!title.includes(searchLower) && !subject.includes(searchLower) && !desc.includes(searchLower)) {
           return false;
         }
       }
       if (pastFilterChannel !== 'all' && p.channel !== pastFilterChannel) return false;
       if (pastFilterEmission !== 'all' && title !== pastFilterEmission.toLowerCase()) return false;
       if (pastFilterMonth !== 'all') {
         const d = new Date(p.dateIso);
         const monthKey = `${d.getFullYear()}-${(d.getMonth()+1).toString().padStart(2, '0')}`;
         if (pastFilterMonth !== monthKey) return false;
       }
       return true;
    }).sort((a,b) => new Date(b.dateIso).getTime() - new Date(a.dateIso).getTime());

  // Extrait les chaînes uniques et mois uniques pour les filtres passés
  const allPastProgs = programsInfo.filter(p => p.published && getProgramStatus(p.dateIso, p.durationMinutes) === 'past');
  const pastChannels = Array.from(new Set(allPastProgs.map(p => p.channel)));
  
  // Emissions available based on the selected channel
  const pastEmissions = Array.from(new Set(allPastProgs
    .filter(p => pastFilterChannel === 'all' || p.channel === pastFilterChannel)
    .map(p => lang === 'fr' ? p.frTitle : lang === 'ar' ? p.arTitle : p.zghTitle)));

  const pastMonths = Array.from(new Set(allPastProgs.map(p => {
    const d = new Date(p.dateIso);
    return `${d.getFullYear()}-${(d.getMonth()+1).toString().padStart(2, '0')}`;
  })));

  const renderCard = (prog: any) => {
    const title = lang === 'fr' ? prog.frTitle : lang === 'ar' ? prog.arTitle : prog.zghTitle;
    const dateStr = lang === 'fr' ? prog.dateFormatted_fr : lang === 'ar' ? prog.dateFormatted_ar : prog.dateFormatted_zgh;
    const typeLabel = lang === 'fr' ? labels.fr[prog.type as keyof typeof labels.fr] : lang === 'ar' ? labels.ar[prog.type as keyof typeof labels.ar] : labels.zgh[prog.type as keyof typeof labels.zgh];

    return (
      <motion.div
        key={prog.id}
        initial={{ opacity: 0, scale: 0.98 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        className="group flex flex-col h-full bg-card rounded-xl border border-border/80 shadow-sm hover:shadow-md transition-shadow overflow-hidden"
      >
        <Link to={`/emissions/${prog.id}`} className="block relative h-32 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 overflow-hidden">
          <img src={getPoster(prog)} alt="" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-black/10 mix-blend-multiply" />
          <div className="absolute inset-0 p-2 sm:p-4 flex justify-between items-start">
            <Badge variant="secondary" className="backdrop-blur-md bg-white/90 text-black font-display font-bold uppercase tracking-wider text-[9px] sm:text-[10px] shadow-sm pointer-events-none relative z-10">
              {getTypeIcon(prog.type)}
              <span className="hidden sm:inline">{typeLabel}</span>
            </Badge>
            <div className="relative z-10 pointer-events-none">{getStatusBadge(prog.statusTracker)}</div>
          </div>
        </Link>
        <CardContent className="p-3 sm:p-5 flex-1 flex flex-col">
          <Link to={`/emissions/${prog.id}`} className="hover:underline focus:outline-none focus:underline mb-2 sm:mb-3 flex-col flex">
            <h3 className="font-display font-bold text-sm sm:text-lg text-foreground leading-snug group-hover:text-primary transition-colors line-clamp-2">
              {title}
            </h3>
            {(lang === 'ar' ? prog.subject_ar : lang === 'zgh' ? prog.subject_zgh : prog.subject_fr) && (
               <span className="text-xs text-muted-foreground mt-1 line-clamp-1">{lang === 'ar' ? prog.subject_ar : lang === 'zgh' ? prog.subject_zgh : prog.subject_fr}</span>
            )}
          </Link>
          <div className="mt-auto">
            <span className="text-[10px] sm:text-sm font-black tracking-wide text-primary/90 mb-2 sm:mb-3 block bg-primary/5 w-fit px-1.5 sm:px-2 py-0.5 rounded-md">{prog.channel}</span>
            <div className="flex flex-col xl:flex-row xl:items-center gap-1 xl:gap-3 text-xs sm:text-sm text-foreground font-body font-bold pt-2 sm:pt-3 border-t border-border/50">
              <span className="flex items-center w-full min-w-0"><Calendar className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-1.5 text-primary/80 shrink-0" /><span className="truncate">{dateStr}</span></span>
              <span className="flex items-center shrink-0" dir="ltr"><Clock className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-1.5 text-primary/80" />{prog.time}</span>
            </div>
          </div>
        </CardContent>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />
      <div className="flex-1 container pt-24 pb-16 max-w-6xl">
        <Button variant="ghost" className="mb-8 font-body -ml-4 hover:bg-transparent hover:text-primary transition-colors" asChild>
          <Link to="/accueil-emissions">
             <ArrowLeft className="w-4 h-4 mr-2 rtl:ml-2 rtl:mr-0 rtl:rotate-180" />
             {lang === 'ar' ? 'العودة للاستقبال' : lang === 'zgh' ? 'ⴰⵖⵓⵍ ⵙ ⵓⵙⵏⵓⴱⴳ' : 'Retour à l\'accueil'}
          </Link>
        </Button>
        <div className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-display font-extrabold text-foreground mb-4">
            {l.pageTitle}
          </h1>
          <p className="text-base sm:text-lg font-body text-muted-foreground max-w-2xl mx-auto px-4 mb-6">
            {l.pageDesc}
          </p>
          <Button variant="secondary" className="font-body font-semibold shadow-sm" asChild>
            <Link to="/proposer-emission">{lang === 'ar' ? 'اقتراح برنامج' : lang === 'zgh' ? 'ⵙⵉⴷⴼ ⴰⵖⴰⵡⴰⵙ' : 'Proposer une émission'}</Link>
          </Button>
        </div>

        {/* Search & Filters */}
        <div className="bg-card p-4 rounded-xl border border-border shadow-sm mb-12 flex flex-col md:flex-row gap-4 items-center">
          <div className="relative flex-1 w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input 
              placeholder={l.searchPlaceholder} 
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 font-body"
            />
          </div>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-full md:w-48 font-body">
              <Filter className="w-4 h-4 mr-2 opacity-50" />
              <SelectValue placeholder={l.filterTypeAll} />
            </SelectTrigger>
            <SelectContent font-body>
              <SelectItem value="all">{l.filterTypeAll}</SelectItem>
              <SelectItem value="tv">{l.tv}</SelectItem>
              <SelectItem value="radio">{l.radio}</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterTime} onValueChange={setFilterTime}>
            <SelectTrigger className="w-full md:w-48 font-body">
              <Calendar className="w-4 h-4 mr-2 opacity-50" />
              <SelectValue placeholder={l.filterTimeAll} />
            </SelectTrigger>
            <SelectContent font-body>
              <SelectItem value="all">{l.filterTimeAll}</SelectItem>
              <SelectItem value="today">{l.filterToday}</SelectItem>
              <SelectItem value="week">{l.filterWeek}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {sortedPrograms.length === 0 && (
          <div className="text-center py-20 bg-muted/20 rounded-2xl border border-dashed border-border">
            <p className="text-muted-foreground font-body">{l.noResults}</p>
          </div>
        )}

        {sortedPrograms.length > 0 && activePrograms.length > 0 && (
          <div className="mb-16">
            <h2 className="text-xl sm:text-2xl font-display font-bold text-foreground mb-4 sm:mb-6 pb-2 border-b border-border flex items-center">
              <span className="w-1.5 sm:w-2 h-5 sm:h-6 bg-primary rounded-sm mr-2 sm:mr-3" />
              {l.upcomingSection}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-6 lg:gap-8">
              {activePrograms.map(renderCard)}
            </div>
          </div>
        )}

        {pastPrograms.length > 0 && (
          <div className="mt-16 mb-8">
            <h2 className="text-xl sm:text-3xl font-display font-bold text-foreground mb-4 sm:mb-8 pb-2 border-b border-border flex items-center">
              <span className="w-1.5 sm:w-2 h-6 sm:h-8 bg-red-600 rounded-sm mr-2 sm:mr-3" />
              La médiathèque Kayn'in
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
              {pastPrograms.slice(0, 8).map(prog => {
                 const title = lang === 'fr' ? prog.frTitle : lang === 'ar' ? prog.arTitle : prog.zghTitle;
                 // On utilise le composant iframe si un lien youtube existe et qu'on a pu extraire l'ID, 
                 // sinon on met la carte normale avec un badge
                 let embedUrl = "";
                 if (prog.youtubeUrl) {
                    const ytMatch = prog.youtubeUrl.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))((\w|-){11})/);
                    if (ytMatch) embedUrl = `https://www.youtube.com/embed/${ytMatch[1]}`;
                 }

                 return (
                   <div key={prog.id} className="bg-card rounded-xl overflow-hidden border border-border shadow-sm flex flex-col group hover:shadow-md transition-shadow">
                     {embedUrl ? (
                       <div className="relative w-full aspect-video bg-black">
                         <iframe 
                           src={embedUrl} 
                           title={title}
                           allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                           allowFullScreen
                           className="absolute top-0 left-0 w-full h-full border-0"
                         />
                       </div>
                     ) : (
                       <Link to={`/emissions/${prog.id}`} className="relative w-full aspect-video bg-muted block overflow-hidden">
                         <img src={getPoster(prog)} alt="" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform" />
                         <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                           <PlayCircle className="w-10 h-10 text-white/80" />
                         </div>
                       </Link>
                     )}
                     <div className="p-3 flex flex-col flex-1">
                       <Link to={`/emissions/${prog.id}`} className="hover:underline">
                         <h3 className="font-display font-bold text-sm text-foreground line-clamp-2 group-hover:text-red-600 transition-colors">
                           {title}
                         </h3>
                         {(lang === 'ar' ? prog.subject_ar : lang === 'zgh' ? prog.subject_zgh : prog.subject_fr) && <span className="text-xs text-muted-foreground block line-clamp-1 mt-0.5">{lang === 'ar' ? prog.subject_ar : lang === 'zgh' ? prog.subject_zgh : prog.subject_fr}</span>}
                       </Link>
                     </div>
                   </div>
                 );
              })}
            </div>

            <div className="mt-12 p-6 bg-card rounded-xl border border-border/60 shadow-sm">
              <h3 className="font-display font-bold text-lg mb-4 flex items-center">
                <Search className="w-5 h-5 mr-2 text-primary" /> Recherche dans les archives
              </h3>
              <div className="flex flex-col md:flex-row gap-4 mb-6">
                 <Input 
                   placeholder="Mots-clés..." 
                   value={pastSearch}
                   onChange={e => setPastSearch(e.target.value)}
                   className="flex-1"
                 />
                 <Select value={pastFilterChannel} onValueChange={(v) => { setPastFilterChannel(v); setPastFilterEmission("all"); }}>
                   <SelectTrigger className="w-full md:w-48">
                     <SelectValue placeholder="Média" />
                   </SelectTrigger>
                   <SelectContent>
                     <SelectItem value="all">Tous les médias</SelectItem>
                     {pastChannels.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                   </SelectContent>
                 </Select>
                 <Select value={pastFilterEmission} onValueChange={setPastFilterEmission}>
                   <SelectTrigger className="w-full md:w-48">
                     <SelectValue placeholder="Émission" />
                   </SelectTrigger>
                   <SelectContent>
                     <SelectItem value="all">Toutes les émissions</SelectItem>
                     {pastEmissions.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                   </SelectContent>
                 </Select>
                 <Select value={pastFilterMonth} onValueChange={setPastFilterMonth}>
                   <SelectTrigger className="w-full md:w-48">
                     <SelectValue placeholder="Mois" />
                   </SelectTrigger>
                   <SelectContent>
                     <SelectItem value="all">Tous les mois</SelectItem>
                     {pastMonths.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                   </SelectContent>
                 </Select>
              </div>

              <div className="space-y-2">
                 {filteredPastPrograms.length === 0 ? (
                   <p className="text-sm text-muted-foreground text-center py-4">Aucune archive trouvée pour cette recherche.</p>
                 ) : (
                   filteredPastPrograms.map(prog => {
                     const t = lang === 'fr' ? prog.frTitle : lang === 'ar' ? prog.arTitle : prog.zghTitle;
                     const link = prog.replayUrl || prog.youtubeUrl || prog.socialLinks?.youtube || prog.socialLinks?.website || `/emissions/${prog.id}`;
                     const isExternal = link.startsWith("http");

                     return (
                       <div key={`arch-${prog.id}`} className="flex items-center justify-between p-3 hover:bg-muted/30 rounded-lg transition-colors border border-transparent hover:border-border/50">
                         <div className="flex flex-col">
                           <span className="font-semibold text-sm flex items-center gap-2">
                             <Badge variant="outline" className="text-[10px] py-0">{prog.channel}</Badge> 
                             {t} {(lang === 'ar' ? prog.subject_ar : lang === 'zgh' ? prog.subject_zgh : prog.subject_fr) && <span className="text-muted-foreground font-normal">- {lang === 'ar' ? prog.subject_ar : lang === 'zgh' ? prog.subject_zgh : prog.subject_fr}</span>}
                           </span>
                           <span className="text-xs text-muted-foreground mt-1">{lang === 'ar' ? prog.dateFormatted_ar : lang === 'zgh' ? prog.dateFormatted_zgh : prog.dateFormatted_fr}</span>
                         </div>
                         {isExternal ? (
                           <a href={link} target="_blank" rel="noopener noreferrer" className="flex items-center text-xs font-bold text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100 px-3 py-1.5 rounded-full transition-colors shrink-0">
                             <Youtube className="w-3.5 h-3.5 mr-1" /> Replay
                           </a>
                         ) : (
                           <Link to={link} className="text-xs font-bold text-primary hover:underline shrink-0">
                             Voir la fiche
                           </Link>
                         )}
                       </div>
                     );
                   })
                 )}
              </div>
            </div>

          </div>
        )}



        {/* Navigation finale */}
        <div className="mt-16 flex flex-col sm:flex-row flex-wrap items-center justify-center gap-3 sm:gap-4 border-t border-border pt-10">
           <Button variant="outline" size="lg" className="w-full sm:w-auto font-body rounded-full" asChild>
             <Link to="/accueil-emissions">{lang === 'ar' ? 'الاستقبال' : 'Accueil'}</Link>
           </Button>
           <Button variant="default" size="lg" className="w-full sm:w-auto bg-primary hover:bg-primary/90 text-white shadow-md font-body rounded-full" asChild>
             <Link to="/agenda-national">{lang === 'ar' ? 'الأجندة الوطنية' : lang === 'zgh' ? 'ⵥⵕ ⵉⵎⵓⵙⵙⵓⵜⵏ (ⴰⵖⴰⵡⴰⵙ ⴰⵏⴰⵎⵓⵔ)' : 'Voir les Évènements (Agenda National)'}</Link>
           </Button>
           <Button variant="secondary" size="lg" className="w-full sm:w-auto font-body rounded-full" asChild>
             <Link to="/partis">{lang === 'ar' ? 'الأحزاب السياسية' : 'Les Partis Politiques'}</Link>
           </Button>
        </div>

      </div>
      <Footer />
    </div>
  );
};

export default AllEmissionsPage;
